import { BRAND_CONFIG } from '@@brand'
import { Shop, ShopTag, TopologyCity } from '@@types'
import { Clusterer, Map, Placemark, YMaps } from '@pbe/react-yandex-maps'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useMediaQuery } from 'usehooks-ts'

import { useLazyShopsInGeoPolygon } from '@entities/Shop/api/shopsInGeoPolygon.gql'
import { useFetchTopology } from '@entities/Shop/api/topology.gql'

import useShopTime from '@shared/hooks/useShopTime'
import Breadcrumbs from '@shared/ui/breadcrumbs'
import Button from '@shared/ui/button'
import Container from '@shared/ui/container'
import Wrapper from '@shared/ui/wrapper'

import { ShopDetails } from './ShopDetails'
import { calculateDistance, formatDistance } from '../lib/distance'

import styles from './ShopsPage.module.scss'

// Типы фильтров по времени работы
type WorkHoursFilter = 'all' | 'open' | 'closed' | '24h'


// Координаты, покрывающие весь земной шар
const GLOBAL_CORNERS = [
  { latitude: -90, longitude: -180 },
  { latitude: -90, longitude: 180 },
  { latitude: 90, longitude: 180 },
  { latitude: 90, longitude: -180 },
]

const DEFAULT_CENTER: [number, number] = [ 51.128207, 71.430411 ] // Астана

// Проверка, открыт ли магазин в данный момент
const isShopCurrentlyOpen = (openTime: number, closeTime: number): boolean => {
  const openSeconds = openTime > 86400 ? Math.floor(openTime / 1000) : openTime
  const closeSeconds = closeTime > 86400 ? Math.floor(closeTime / 1000) : closeTime
  const now = new Date()
  const currentTimeInSeconds = now.getHours() * 3600 + now.getMinutes() * 60
  return currentTimeInSeconds >= openSeconds && currentTimeInSeconds < closeSeconds
}

// Проверка, работает ли магазин 24 часа
const isShop24h = (openTime: number, closeTime: number): boolean => {
  const openSeconds = openTime > 86400 ? Math.floor(openTime / 1000) : openTime
  const closeSeconds = closeTime > 86400 ? Math.floor(closeTime / 1000) : closeTime
  // 24 часа: открытие в 00:00 (0) и закрытие в 23:59 (86340) или 24:00 (86400)
  return openSeconds === 0 && closeSeconds >= 86340
}

interface ShopItemProps {
  shop: Shop
  onClick: (shop: Shop) => void
  isSelected: boolean
  userLocation?: { lat: number; lng: number } | null
}

const ShopItem = ({ shop, onClick, isSelected, userLocation }: ShopItemProps) => {
  const { t: ts } = useTranslation('shops')
  const { open, close, getShopStatus } = useShopTime(
    shop?.open_time || 0,
    shop?.close_time || 0,
  )

  const distance = useMemo(() => {
    if (!userLocation || !shop?.shopGeoPoint?.latitude || !shop?.shopGeoPoint?.longitude) {
      return null
    }
    return calculateDistance(
      userLocation.lat,
      userLocation.lng,
      shop.shopGeoPoint.latitude,
      shop.shopGeoPoint.longitude
    )
  }, [ userLocation, shop?.shopGeoPoint ])

  return (
    <li
      onClick={() => onClick(shop)}
      className={classNames(styles.shopItem, {
        [styles.shopItem_selected]: isSelected,
      })}
    >
      <div className={styles.shopItem__header}>
        <span className={styles.shopItem__address}>{shop?.address}</span>
        {distance !== null && (
          <span className={styles.shopItem__distance}>{formatDistance(distance)}</span>
        )}
      </div>
      
      {shop?.shopTags && shop.shopTags.length > 0 && (
        <div className={styles.shopItem__tags}>
          {shop.shopTags.map((tag) => (
            <span key={tag.id} className={styles.shopItem__tag}>
              {tag.name}
            </span>
          ))}
        </div>
      )}
      
      <div className={styles.shopItem__schedule}>
        {getShopStatus ? (
          <span className={styles.shopItem__scheduleText}>
            {ts('open_hours', { open, close })}
          </span>
        ) : (
          <>
            <span className={styles.shopItem__statusClosed}>{ts('closed')}</span>
            <span className={styles.shopItem__separator} />
            <span className={styles.shopItem__opensAt}>{ts('opens_at', { time: open })}</span>
          </>
        )}
      </div>
    </li>
  )
}

interface MobileShopInfoProps {
  shop: Shop
  onClose: () => void
  userLocation?: { lat: number; lng: number } | null
}

const MobileShopInfo = ({ shop, onClose, userLocation }: MobileShopInfoProps) => {
  const { t: ts } = useTranslation('shops')
  const [ currentImageIndex, setCurrentImageIndex ] = useState(0)
  const { open, close, getShopStatus } = useShopTime(
    shop?.open_time || 0,
    shop?.close_time || 0,
  )

  const distance = useMemo(() => {
    if (!userLocation || !shop?.shopGeoPoint?.latitude || !shop?.shopGeoPoint?.longitude) {
      return null
    }
    return calculateDistance(
      userLocation.lat,
      userLocation.lng,
      shop.shopGeoPoint.latitude,
      shop.shopGeoPoint.longitude
    )
  }, [ userLocation, shop?.shopGeoPoint ])

  const handleRoute = () => {
    const { latitude, longitude } = shop.shopGeoPoint || {}
    if (latitude && longitude) {
      const url = `https://yandex.ru/maps/?rtext=~${latitude},${longitude}&rtt=auto`
      window.open(url, '_blank')
    }
  }

  const images = shop.images || []
  const hasImages = images.length > 0

  return (
    <div className={styles.mobileShopInfo}>
      {/* Header */}
      <div className={styles.mobileShopInfo__header}>
        <button className={styles.mobileShopInfo__back} onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {ts('back_to_list')}
        </button>
        <button className={styles.mobileShopInfo__close} onClick={onClose}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>
      </div>

      {/* Image Carousel */}
      {hasImages && (
        <>
          <div className={styles.mobileShopInfo__carousel}>
            <img 
              src={images[currentImageIndex]?.url} 
              alt={shop.name}
              className={styles.mobileShopInfo__carouselImage}
            />
            {images.length > 1 && (
              <>
                <button 
                  className={classNames(styles.mobileShopInfo__carouselBtn, styles.mobileShopInfo__carouselBtn_prev)}
                  onClick={() => setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1))}
                >
                  <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                    <path d="M7 1L1 7L7 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button 
                  className={classNames(styles.mobileShopInfo__carouselBtn, styles.mobileShopInfo__carouselBtn_next)}
                  onClick={() => setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0))}
                >
                  <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                    <path d="M1 1L7 7L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className={styles.mobileShopInfo__carouselDots}>
              {images.map((_, index) => (
                <span 
                  key={index}
                  className={classNames(styles.mobileShopInfo__carouselDot, {
                    [styles.mobileShopInfo__carouselDot_active]: index === currentImageIndex,
                  })}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Content */}
      <div className={styles.mobileShopInfo__content}>
        <div className={styles.mobileShopInfo__addressRow}>
          <h3 className={styles.mobileShopInfo__address}>{shop.address}</h3>
          {distance !== null && (
            <span className={styles.mobileShopInfo__distance}>{formatDistance(distance)}</span>
          )}
        </div>

        {/* Tags */}
        {shop.shopTags && shop.shopTags.length > 0 && (
          <div className={styles.mobileShopInfo__tags}>
            {shop.shopTags.map((tag) => (
              <span key={tag.id} className={styles.mobileShopInfo__tag}>
                {tag.icon?.url && (
                  <img src={tag.icon.url} alt="" className={styles.mobileShopInfo__tagIcon} />
                )}
                {tag.name}
              </span>
            ))}
          </div>
        )}

        {/* Info rows */}
        <div className={styles.mobileShopInfo__info}>
          <div className={styles.mobileShopInfo__infoRow}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M12 6V12L15 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div className={styles.mobileShopInfo__infoContent}>
              <span className={styles.mobileShopInfo__schedule}>
                {ts('daily_hours', { open, close })}
              </span>
              <span 
                className={classNames(styles.mobileShopInfo__status, {
                  [styles.mobileShopInfo__status_open]: getShopStatus,
                })}
              >
                {getShopStatus ? ts('status_open') : ts('status_closed')}
              </span>
            </div>
          </div>

          {shop.phone && (
            <div className={styles.mobileShopInfo__infoRow}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M21 16.5V19C21 19.55 20.55 20 20 20H19.5C11 20 4 13 4 4.5V4C4 3.45 4.45 3 5 3H7.5L9.5 8L7.5 10C8.5 12 10 13.5 12 14.5L14 12.5L19 14.5L21 16.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <a href={`tel:${shop.phone}`} className={styles.mobileShopInfo__phone}>
                {shop.phone}
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Buttons */}
      <div className={styles.mobileShopInfo__buttons}>
        <Button theme="primary" onClick={handleRoute}>
          {ts('route')}
        </Button>
        {shop.isPickupDeliveryAvailable && (
          <Button theme="grey">
            {ts('select_pickup')}
          </Button>
        )}
      </div>
    </div>
  )
}

export default function ShopsPage () {
  const { t: ts } = useTranslation('shops')
  const isMobile = useMediaQuery('(max-width: 768px)')
  
  const mapRef = useRef<ymaps.Map | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const [ isMapReady, setMapReady ] = useState(false)
  const [ shops, setShops ] = useState<Shop[]>([])
  const [ selectedShop, setSelectedShop ] = useState<Shop | null>(null)
  const [ searchQuery, setSearchQuery ] = useState('')
  const [ selectedTags, setSelectedTags ] = useState<string[]>([])
  const [ showList, setShowList ] = useState(true)
  const [ selectedCity, setSelectedCity ] = useState<TopologyCity | null>(null)
  const [ showCityDropdown, setShowCityDropdown ] = useState(false)
  const [ userLocation, setUserLocation ] = useState<{ lat: number; lng: number } | null>(null)
  const [ showFilters, setShowFilters ] = useState(false)
  const [ workHoursFilter, setWorkHoursFilter ] = useState<WorkHoursFilter>('all')
  const [ mobileView, setMobileView ] = useState<'list' | 'map'>('list')
  
  const { getCities, loading: topologyLoading } = useFetchTopology()
  const [ fetchShops, { data, loading: shopsLoading } ] = useLazyShopsInGeoPolygon()
  
  const cities = useMemo(() => getCities(), [ getCities ])
  const loading = topologyLoading || shopsLoading

  // Устанавливаем город по умолчанию
  useEffect(() => {
    if (cities.length > 0 && !selectedCity) {
      setSelectedCity(cities[0])
    }
  }, [ cities, selectedCity ])

  // Получаем геолокацию пользователя (только с разрешения)
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          })
        },
        () => {
          // Геолокация отклонена — скрываем расстояние
          setUserLocation(null)
        }
      )
    }
  }, [])

  // Загружаем магазины один раз при монтировании
  useEffect(() => {
    fetchShops({ variables: { corners: GLOBAL_CORNERS } })
  }, [ fetchShops ])

  // Сохраняем магазины в стейт
  useEffect(() => {
    if (data?.shopsInGeoPolygon) {
      setShops(data.shopsInGeoPolygon)
    }
  }, [ data?.shopsInGeoPolygon ])

  // Получаем уникальные теги из всех магазинов
  const allTags = useMemo(() => {
    const tagsMap: Record<string, ShopTag> = {}
    shops.forEach((shop) => {
      shop.shopTags?.forEach((tag) => {
        const tagId = String(tag.id)
        if (!tagsMap[tagId]) {
          tagsMap[tagId] = tag
        }
      })
    })
    return Object.values(tagsMap)
  }, [ shops ])

  // Фильтруем магазины по поиску, тегам, городу и времени работы (для карты)
  const filteredShops = useMemo(() => {
    return shops.filter((shop) => {
      const searchLower = searchQuery.toLowerCase()
      const matchesSearch = !searchQuery || 
        shop.address?.toLowerCase().includes(searchLower) ||
        shop.name?.toLowerCase().includes(searchLower) ||
        shop.customerName?.toLowerCase().includes(searchLower)

      const matchesTags = selectedTags.length === 0 || 
        shop.shopTags?.some((tag) => selectedTags.includes(String(tag.id)))

      // Фильтр по городу (по имени в адресе)
      const matchesCity = !selectedCity || 
        shop.address?.toLowerCase().includes(selectedCity.name.toLowerCase())

      // Фильтр по времени работы
      let matchesWorkHours = true
      if (workHoursFilter !== 'all') {
        const openTime = shop?.open_time || 0
        const closeTime = shop?.close_time || 0
        const isOpen = isShopCurrentlyOpen(openTime, closeTime)
        const is24h = isShop24h(openTime, closeTime)
        
        switch (workHoursFilter) {
        case 'open':
          matchesWorkHours = isOpen
          break
        case 'closed':
          matchesWorkHours = !isOpen
          break
        case '24h':
          matchesWorkHours = is24h
          break
        }
      }

      return matchesSearch && matchesTags && matchesCity && matchesWorkHours
    })
  }, [ shops, searchQuery, selectedTags, selectedCity, workHoursFilter ])

  // Центрируем карту при выборе магазина
  useEffect(() => {
    if (selectedShop && isMapReady && mapRef.current) {
      const { latitude, longitude } = selectedShop.shopGeoPoint || {}
      if (latitude && longitude) {
        mapRef.current.setCenter([ latitude, longitude ], 15, {
          duration: 300,
        })
      }
    }
  }, [ selectedShop, isMapReady ])

  const handleShopClick = (shop: Shop) => {
    setSelectedShop(shop)
  }

  const handleTagToggle = useCallback((tagId: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [ ...prev, tagId ]
    )
  }, [])

  const handleResetFilters = useCallback(() => {
    setSelectedTags([])
    setWorkHoursFilter('all')
  }, [])

  const hasActiveFilters = selectedTags.length > 0 || workHoursFilter !== 'all'

  const handlePlacemarkClick = (shop: Shop) => {
    setSelectedShop(shop)
  }

  const handleCitySelect = (city: TopologyCity) => {
    setSelectedCity(city)
    setShowCityDropdown(false)
    setSelectedShop(null)
  }

  const toggleList = () => {
    setShowList((prev) => !prev)
  }

  // Центрируем карту при загрузке магазинов или смене города
  useEffect(() => {
    // Не двигаем карту к границам, если выбран конкретный магазин
    if (selectedShop) return
    if (!isMapReady || !mapRef.current || filteredShops.length === 0) return

    const points = filteredShops
      .filter((shop) => shop?.shopGeoPoint?.latitude && shop?.shopGeoPoint?.longitude)
      .map((shop) => [ shop.shopGeoPoint!.latitude, shop.shopGeoPoint!.longitude ] as [number, number])

    if (points.length === 0) return

    if (points.length === 1) {
      mapRef.current.setCenter(points[0], 14, { duration: 300 })
      return
    }

    // Вычисляем границы
    const lats = points.map((p) => p[0])
    const lngs = points.map((p) => p[1])
    const bounds: [[number, number], [number, number]] = [
      [ Math.min(...lats), Math.min(...lngs) ],
      [ Math.max(...lats), Math.max(...lngs) ],
    ]

    mapRef.current.setBounds(bounds, {
      checkZoomRange: true,
      duration: 300,
      zoomMargin: [ 50, 50, 50, 50 ],
    })
  }, [ filteredShops, isMapReady, selectedShop ])

  return (
    <Container>
      <Wrapper>
        <Breadcrumbs
          className={styles.breadcrumbs}
          links={[
            { title: ts('page') },
          ]}
        />

        <h1 className={styles.title}>
          {ts('title_in')}{' '}
          <span className={styles.titleCityWrapper}>
            <button 
              className={styles.citySelector}
              onClick={() => setShowCityDropdown(!showCityDropdown)}
            >
              {selectedCity?.name || ts('select_city')}
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" className={classNames(styles.citySelector__arrow, { [styles.citySelector__arrow_open]: showCityDropdown })}>
                <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {showCityDropdown && (
              <div className={styles.cityDropdown}>
                {cities.map((city) => (
                  <button
                    key={city.id}
                    className={classNames(styles.cityDropdown__item, {
                      [styles.cityDropdown__item_active]: selectedCity?.id === city.id,
                    })}
                    onClick={() => handleCitySelect(city)}
                  >
                    {city.name}
                  </button>
                ))}
              </div>
            )}
          </span>
        </h1>

        {/* Mobile View Switcher */}
        {isMobile && (
          <div className={styles.viewSwitcher}>
            <button
              className={classNames(styles.viewSwitcher__tab, {
                [styles.viewSwitcher__tab_active]: mobileView === 'list',
              })}
              onClick={() => setMobileView('list')}
            >
              {ts('list')}
            </button>
            <button
              className={classNames(styles.viewSwitcher__tab, {
                [styles.viewSwitcher__tab_active]: mobileView === 'map',
              })}
              onClick={() => setMobileView('map')}
            >
              {ts('map')}
            </button>
          </div>
        )}

        <div className={classNames(styles.mapContainer, {
          [styles.mapContainer_mobileList]: isMobile && mobileView === 'list',
          [styles.mapContainer_mobileMap]: isMobile && mobileView === 'map',
        })}>
          {/* Карта */}
          <div className={classNames(styles.map, {
            [styles.map_hidden]: isMobile && mobileView === 'list',
          })}>
            {loading ? (
              <SkeletonTheme>
                <Skeleton height="100%" style={{ borderRadius: 20 }} />
              </SkeletonTheme>
            ) : (
              <YMaps
                query={{
                  apikey: import.meta.env.VITE_YANDEX_MAPS_API_KEY,
                  load: 'package.full',
                }}
              >
                <Map
                  defaultState={{
                    center: DEFAULT_CENTER,
                    zoom: 4,
                    controls: [],
                  }}
                  width="100%"
                  height="100%"
                  instanceRef={(ref) => {
                    if (ref) {
                      mapRef.current = ref
                      if (!isMapReady) {
                        setMapReady(true)
                      }
                    }
                  }}
                  options={{
                    suppressMapOpenBlock: true,
                    copyrightLogoVisible: false,
                    copyrightUaVisible: false,
                  }}
                >
                  <Clusterer
                    options={{
                      clusterIconColor: '#e84e29',
                      groupByCoordinates: false,
                      clusterDisableClickZoom: false,
                      clusterOpenBalloonOnClick: false,
                    }}
                  >
                    {filteredShops.map((shop) => {
                      const latitude = shop?.shopGeoPoint?.latitude
                      const longitude = shop?.shopGeoPoint?.longitude
                      if (latitude == null || longitude == null) return null

                      const isSelected = selectedShop?.id === shop.id

                      return (
                        <Placemark
                          onClick={() => handlePlacemarkClick(shop)}
                          key={shop?.id}
                          geometry={[ latitude, longitude ]}
                          options={{
                            iconLayout: 'default#image',
                            iconImageHref: isSelected 
                              ? BRAND_CONFIG.images.mapPinActive 
                              : BRAND_CONFIG.images.mapPin,
                            iconImageSize: isSelected ? [ 40, 50 ] : [ 32, 40 ],
                            iconImageOffset: isSelected ? [ -20, -50 ] : [ -16, -40 ],
                            zIndex: isSelected ? 1000 : 1,
                          }}
                        />
                      )
                    })}
                  </Clusterer>
                </Map>
              </YMaps>
            )}

            {/* Контролы карты */}
            {(!isMobile || mobileView === 'map') && (
              <div className={styles.mapControls}>
                <button 
                  className={styles.mapControls__btn}
                  onClick={() => mapRef.current?.setZoom((mapRef.current?.getZoom() || 12) + 1)}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
                <button 
                  className={styles.mapControls__btn}
                  onClick={() => mapRef.current?.setZoom((mapRef.current?.getZoom() || 12) - 1)}
                >
                  <svg width="14" height="2" viewBox="0 0 14 2" fill="none">
                    <path d="M1 1H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
                <button 
                  className={styles.mapControls__btn}
                  onClick={() => {
                    if (userLocation && mapRef.current) {
                      mapRef.current.setCenter([ userLocation.lat, userLocation.lng ], 14)
                    }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M8 10C9.10457 10 10 9.10457 10 8C10 6.89543 9.10457 6 8 6C6.89543 6 6 6.89543 6 8C6 9.10457 6.89543 10 8 10Z" fill="currentColor"/>
                  </svg>
                </button>
              </div>
            )}

            {/* Mobile Map Search Bar */}
            {isMobile && mobileView === 'map' && (
              <div className={styles.mobileMapSearch}>
                <div className={styles.searchInput}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={styles.searchInput__icon}>
                    <circle cx="11" cy="11" r="7" stroke="#8F8D8D" strokeWidth="2"/>
                    <path d="M20 20L16 16" stroke="#8F8D8D" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <input
                    type="text"
                    placeholder={ts('search_placeholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <button 
                  className={classNames(styles.filterBtn, { [styles.filterBtn_active]: hasActiveFilters })}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M3 6H17M6 10H14M9 14H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            )}

            {/* Mobile Shop Info Panel (appears when shop is selected on map) */}
            {isMobile && mobileView === 'map' && selectedShop && (
              <MobileShopInfo
                shop={selectedShop}
                onClose={() => setSelectedShop(null)}
                userLocation={userLocation}
              />
            )}
          </div>

          {/* Панель списка магазинов */}
          <div 
            ref={listRef}
            className={classNames(styles.listPanel, {
              [styles.listPanel_collapsed]: !showList && !isMobile,
              [styles.listPanel_hidden]: isMobile && mobileView === 'map',
            })}
          >
            {/* Кнопка сворачивания */}
            {!isMobile && (
              <button className={styles.collapseBtn} onClick={toggleList}>
                <svg 
                  width="8" 
                  height="14" 
                  viewBox="0 0 8 14" 
                  fill="none"
                  className={classNames(styles.collapseBtn__icon, { [styles.collapseBtn__icon_collapsed]: !showList })}
                >
                  <path d="M7 1L1 7L7 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}

            {showList && (
              <>
                {/* Поиск и фильтры */}
                <div className={styles.searchBar}>
                  <div className={styles.searchInput}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={styles.searchInput__icon}>
                      <circle cx="11" cy="11" r="7" stroke="#8F8D8D" strokeWidth="2"/>
                      <path d="M20 20L16 16" stroke="#8F8D8D" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <input
                      type="text"
                      placeholder={ts('search_placeholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <button 
                    className={classNames(styles.filterBtn, { [styles.filterBtn_active]: hasActiveFilters })}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M3 6H17M6 10H14M9 14H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                </div>

                {/* Панель фильтров */}
                {showFilters && (
                  <div className={styles.filterPanel}>
                    <div className={styles.filterPanel__header}>
                      <span className={styles.filterPanel__title}>{ts('filters')}</span>
                      {hasActiveFilters && (
                        <button 
                          className={styles.filterPanel__reset}
                          onClick={handleResetFilters}
                        >
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                          {ts('reset')}
                        </button>
                      )}
                    </div>

                    {/* Теги магазинов */}
                    {allTags.length > 0 && (
                      <div className={styles.filterPanel__tags}>
                        {allTags.map((tag) => (
                          <button
                            key={tag.id}
                            className={classNames(styles.filterChip, {
                              [styles.filterChip_active]: selectedTags.includes(String(tag.id)),
                            })}
                            onClick={() => handleTagToggle(String(tag.id))}
                          >
                            {tag.name}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Фильтр по времени работы */}
                    <div className={styles.filterPanel__workHours}>
                      <button
                        className={classNames(styles.filterChip, {
                          [styles.filterChip_active]: workHoursFilter === 'open',
                        })}
                        onClick={() => setWorkHoursFilter(workHoursFilter === 'open' ? 'all' : 'open')}
                      >
                        {ts('filter_open')}
                      </button>
                      <button
                        className={classNames(styles.filterChip, {
                          [styles.filterChip_active]: workHoursFilter === 'closed',
                        })}
                        onClick={() => setWorkHoursFilter(workHoursFilter === 'closed' ? 'all' : 'closed')}
                      >
                        {ts('filter_closed')}
                      </button>
                      <button
                        className={classNames(styles.filterChip, {
                          [styles.filterChip_active]: workHoursFilter === '24h',
                        })}
                        onClick={() => setWorkHoursFilter(workHoursFilter === '24h' ? 'all' : '24h')}
                      >
                        {ts('filter_24h')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Список магазинов */}
                <div className={styles.shopList}>
                  {loading ? (
                    <SkeletonTheme>
                      <Skeleton count={5} height={80} style={{ borderRadius: 0, marginBottom: 0 }} />
                    </SkeletonTheme>
                  ) : filteredShops.length > 0 ? (
                    <ul className={styles.shopList__items}>
                      {filteredShops.map((shop) => (
                        <ShopItem
                          key={shop.id}
                          shop={shop}
                          onClick={handleShopClick}
                          isSelected={selectedShop?.id === shop.id}
                          userLocation={userLocation}
                        />
                      ))}
                    </ul>
                  ) : (
                    <p className={styles.shopList__empty}>{ts('empty')}</p>
                  )}
                </div>
              </>
            )}

            {/* Детали выбранного магазина */}
            {selectedShop && (
              <ShopDetails
                shop={selectedShop}
                onClose={() => setSelectedShop(null)}
                userLocation={userLocation}
                onShowOnMap={isMobile ? () => {
                  setMobileView('map')
                  // Центрируем на магазин после переключения на карту
                  setTimeout(() => {
                    if (mapRef.current && selectedShop.shopGeoPoint) {
                      mapRef.current.setCenter(
                        [ selectedShop.shopGeoPoint.latitude, selectedShop.shopGeoPoint.longitude ],
                        15,
                        { duration: 300 }
                      )
                    }
                  }, 100)
                } : undefined}
              />
            )}
          </div>
        </div>
      </Wrapper>
    </Container>
  )
}
